import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Box, Button } from "@amzn/awsui-components-react/polaris";

import CustomMultiHybridInputAndSelect from "../Templates/components/CustomMultiHybridInputAndSelect";
import { useSuggestedLogins } from "src/hooks/useSuggestedLogins";

// Type options per role. The shared CustomMultiHybridInputAndSelect only shows
// the phonetool alias autosuggest when the selected type value is exactly
// "UPLOADER" or "APPROVER"; "*_LDAP" renders a plain text input for the group.
const TYPE_OPTIONS = {
  preparer: [
    { label: "USER", value: "UPLOADER" },
    { label: "LDAP", value: "UPLOADER_LDAP" },
  ],
  reviewer: [
    { label: "USER", value: "APPROVER" },
    { label: "LDAP", value: "APPROVER_LDAP" },
  ],
  approver: [
    { label: "USER", value: "APPROVER" },
    { label: "LDAP", value: "APPROVER_LDAP" },
  ],
};

/**
 * ag-grid popup cell editor for the Preparer / Reviewer / Approver columns.
 *
 * Renders the shared CustomMultiHybridInputAndSelect so admins get the same
 * USER/LDAP type picker, alias autosuggest, and add/remove-multiple experience
 * used in the CRA deliverables control panel. Committed value is an array of
 * `{ alias, type[, column_index] }` rows.
 *
 * cellEditorParams: { role: "preparer"|"reviewer"|"approver", columnIndex? }
 */
const RoleAliasCellEditor = forwardRef((props, ref) => {
  const role = props.role || "preparer";
  const columnIndex = props.columnIndex;
  const options = TYPE_OPTIONS[role] || TYPE_OPTIONS.preparer;

  // Keep a pristine copy because reactive editor updates can replace
  // props.value while the popup is open. The X button must restore the value
  // from when editing started, not the latest working value.
  const initialRowsRef = useRef(
    Array.isArray(props.value) ? JSON.parse(JSON.stringify(props.value)) : []
  );
  const rowsRef = useRef(JSON.parse(JSON.stringify(initialRowsRef.current)));
  // Force a render only to keep React state in sync; the editor reads rowsRef.
  const [, setTick] = useState(0);

  const { suggestedLogins, aliasesLoading, getSuggestedLoginsCaller } = useSuggestedLogins();

  const normalizeRows = (values) => {
    const seen = new Set();
    return (Array.isArray(values) ? values : [])
      .filter((row) => row && row.type && String(row.alias || "").trim())
      .filter((row) => {
        const key = `${String(row.alias).trim()}|${row.type}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((row) => {
        const clean = { alias: String(row.alias).trim(), type: row.type };
        if (columnIndex != null) clean.column_index = columnIndex;
        return clean;
      });
  };

  useImperativeHandle(ref, () => ({
    getValue() {
      return normalizeRows(rowsRef.current);
    },
    // Render as a popup anchored to the cell rather than an in-cell editor.
    isPopup() {
      return true;
    },
  }));

  const getValues = () => rowsRef.current;
  const updateValue = (_field, values) => {
    rowsRef.current = values;
    props.onValueChange?.(normalizeRows(values));
    setTick((tick) => tick + 1);
  };
  const getOptions = () => options;

  const stopEditing = () => {
    if (props.stopEditing) {
      props.stopEditing();
    } else {
      props.api?.stopEditing();
    }
  };

  const commit = () => stopEditing();

  const cancel = () => {
    rowsRef.current = JSON.parse(JSON.stringify(initialRowsRef.current));
    props.onValueChange?.(normalizeRows(rowsRef.current));
    stopEditing();
  };

  const ROLE_TITLE = { preparer: "Preparer", reviewer: "Reviewer", approver: "Approver" };

  return (
    <div
      style={{
        minWidth: 440,
        maxWidth: 600,
        background: "#ffffff",
        border: "1px solid #d5dbdb",
        borderRadius: 8,
        boxShadow: "0 2px 8px rgba(0, 7, 22, 0.18)",
        padding: 16,
        // Sit above the grid rows so nothing bleeds through the popup.
        zIndex: 1,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <Box variant="h4" padding="n">
          {ROLE_TITLE[role] || "Edit"}
        </Box>
        <Button variant="icon" iconName="close" ariaLabel="Cancel changes" onClick={cancel} />
      </div>

      <CustomMultiHybridInputAndSelect
        field={role}
        selectKey="type"
        inputKey="alias"
        placeholder="alias / group"
        selectHint="USER / LDAP"
        getValues={getValues}
        updateValue={updateValue}
        getOptions={getOptions}
        duplicatesNotAllowed={true}
        loginOptions={suggestedLogins}
        getLoginOptions={getSuggestedLoginsCaller}
        aliasesLoading={aliasesLoading}
      />

      <div style={{ marginTop: 12, textAlign: "right" }}>
        <Button variant="primary" onClick={commit}>
          Done
        </Button>
      </div>
    </div>
  );
});

export default RoleAliasCellEditor;
